/**
 * Users Table component.
 *
 * Displays a tabular view of user data with status indicators and action buttons.
 * This component is used in the admin panel to provide a comprehensive overview of all
 * users, allowing administrators to quickly scan information and manage users.
 * It supports internationalization and responsive design for various screen sizes.
 */

import React from 'react';
import type { User } from '../types/user';
import { useTranslation } from 'react-i18next';

interface UsersTableProps {
  /** Array of user objects to display in the table */
  users: User[];
  /** Callback function triggered when the edit button is clicked for a user */
  onEdit: (user: User) => void;
  /** Callback function triggered when the delete button is clicked for a user */
  onDelete: (user: User) => void;
}

/**
 * Formats a date string into a localized date format.
 * @param dateString - ISO date string to format
 * @returns Localized date string
 */
const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString();
};

/**
 * Gets the initials from a user's name and lastname.
 * @param name - User's first name
 * @param lastname - User's last name
 * @returns Initials string (e.g., "AB" for Alice Brown)
 */
const getInitials = (name: string, lastname: string): string => {
  const firstInitial = name.charAt(0).toUpperCase();
  const lastInitial = lastname.charAt(0).toUpperCase();
  return `${firstInitial}${lastInitial}`;
};

/**
 * Table component for displaying user information in a structured format.
 *
 * Features include:
 * - Responsive table layout with DaisyUI styling
 * - Status badges with color coding
 * - Avatar display with initials fallback
 * - Formatted date display
 * - Action buttons for editing and deleting
 * - Internationalized headers and labels
 *
 * @param props - The component props
 * @returns A responsive table displaying user data
 */
const UsersTable: React.FC<UsersTableProps> = ({ users, onEdit, onDelete }) => {
  const { t } = useTranslation('admin');

  return (
    <div className="overflow-x-auto">
      <table className="table table-zebra w-full">
        <thead>
          <tr>
            <th>{t('users.avatar')}</th>
            <th>{t('users.fullName')}</th>
            <th>{t('users.email')}</th>
            <th>{t('users.phone')}</th>
            <th>{t('users.status')}</th>
            <th>{t('users.referenceCode')}</th>
            <th>{t('users.createdAt')}</th>
            <th>{t('users.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.id}>
              <td>
                {user.avatarUrl ? (
                  <div className="avatar">
                    <div className="w-10 h-10 rounded-full">
                      <img src={user.avatarUrl} alt={`${user.name} ${user.lastname}`} />
                    </div>
                  </div>
                ) : (
                  <div className="avatar placeholder">
                    <div className="bg-neutral text-neutral-content w-10 h-10 rounded-full flex items-center justify-center">
                      <span className="text-xs">{getInitials(user.name, user.lastname)}</span>
                    </div>
                  </div>
                )}
              </td>
              <td>
                {user.name} {user.lastname}
              </td>
              <td>{user.email}</td>
              <td>{user.phone ?? '—'}</td>
              <td>
                <span className={`badge ${user.isActive ? 'badge-success' : 'badge-error'}`}>
                  {user.isActive ? t('users.statusActive') : t('users.statusInactive')}
                </span>
              </td>
              <td>{user.referenceCode ?? '—'}</td>
              <td>{formatDate(user.createdAt)}</td>
              <td>
                <div className="flex gap-2">
                  <button
                    className="btn btn-sm btn-primary"
                    aria-label={t('users.edit')}
                    onClick={() => onEdit(user)}
                  >
                    {t('users.edit')}
                  </button>
                  <button
                    className="btn btn-sm btn-error"
                    aria-label={t('users.delete')}
                    onClick={() => onDelete(user)}
                  >
                    {t('users.delete')}
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default UsersTable;
